import { Grid, Typography, RadioGroup, Radio, FormControlLabel, FormControl, TextField as MuiTextField, FormLabel, MenuItem } from "@material-ui/core";
import * as yup from 'yup';
import { useFormikContext, Field, ErrorMessage } from "formik";
import { TextField, Checkbox } from 'formik-material-ui';
import { DatePicker } from 'formik-material-ui-pickers';
import { ProductSearch } from "components";
import { newTicketContext, newTicketSchema } from '../NewTicketContext';
import { useContext } from "react";
import { applicationContext } from "helpers/services";
import { ReferenceData } from "helpers/enums";
import { Autocomplete } from "formik-material-ui-lab";
import { KeyValuePair } from "helpers";

let ProductInfo = () => {
    let { referenceData } = useContext(applicationContext);
    let { productSuppliers, updateProductSuppliers } = useContext(newTicketContext);

    type Ticket = yup.InferType<typeof newTicketSchema>;
    let form = useFormikContext<Ticket>();

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Please provide these additional info</Typography>
            </Grid>
            <Grid container item xs={12}>
                <Field
                    component={TextField}
                    name="deliveryNote"
                    variant="outlined"
                    fullWidth
                    label="Delivery Note"
                />
            </Grid>
            <Grid container>
                <Grid item xs>
                    <Field
                        component={DatePicker}
                        name="invoiceDate"
                        fullWidth
                        format="LL"
                        inputVariant="outlined"
                        label="Invoice Date"
                    />
                </Grid>
                <Grid item xs>
                    <Field
                        component={TextField}
                        name="invoiceNumber"
                        fullWidth
                        variant="outlined"
                        label="Invoice Number"
                    />
                </Grid>
            </Grid>
            <Grid container alignItems="center">
                <Grid item xs={9}>
                    <ProductSearch
                        label="Product"
                        freeSolo={false}
                        value={{
                            value: form.values.productSku,
                            label: `${form.values.productSku} ${form.values.productDescription}`.trim(),
                            brand: form.values.productBrand,
                            shortDescription: form.values.productDescription,
                            sku: form.values.productSku,
                            hasSerialNumber: form.values.hasSerialNumber,
                            suppliers: []
                        }}
                        onChange={(sku: string, brand: string, description: string, hasSerialNumber: boolean, suppliers: KeyValuePair<string, string>[]) => {
                            form.setFieldValue('productSku', sku);
                            form.setFieldValue('productBrand', brand);
                            form.setFieldValue('productDescription', description);
                            form.setFieldValue('hasSerialNumber', hasSerialNumber);

                            updateProductSuppliers(suppliers);

                            if (suppliers.length === 1) {
                                form.setFieldValue('productSupplierCode', suppliers[0].key);
                                form.setFieldValue('productSupplierName', suppliers[0].value);
                            }
                        }}
                        error={Boolean(form.touched.productSku && form.errors.productSku)}
                        helperText={form.touched.productSku ? form.errors.productSku : ''}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Field
                        component={TextField}
                        name="productQuantity"
                        fullWidth
                        variant="outlined"
                        label="Quantity"
                        type="number"
                    />
                </Grid>

                {productSuppliers.length > 1 &&
                    <Grid item xs={12}>
                        <Field
                            component={TextField}
                            select
                            name="productSupplierCode"
                            variant="outlined"
                            fullWidth
                            label="Product supplier"
                            helperText='Multiple suppliers'
                            onChange={(e: any) => {
                                form.setFieldValue('productSupplierCode', e.target.value);
                                form.setFieldValue('productSupplierName', productSuppliers.getValueByKey(e.target.value));
                            }}
                        >
                            <MenuItem key='' value=''>Any</MenuItem>
                            {productSuppliers.map((supplier, supplierIdx) =>
                                <MenuItem key={supplierIdx} value={supplier.key}>{supplier.value}</MenuItem>
                            )}
                        </Field>
                    </Grid>
                }

                <Grid item xs>
                    <FormControl error={form.touched.productIsNew && form.errors.productIsNew ? true : false}>
                        <FormLabel>Product condition</FormLabel>
                        <RadioGroup
                            row
                            value={form.values.productIsNew}
                            onChange={e => form.setFieldValue('productIsNew', e.target.value)}
                        >
                            <FormControlLabel value='new' control={<Radio />} label="New" />
                            <FormControlLabel value='used' control={<Radio />} label="Used" />
                        </RadioGroup>
                        <ErrorMessage name="productIsNew">
                            {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                        </ErrorMessage>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <Field
                        fullWidth
                        name="purchaseCountry"
                        component={Autocomplete}
                        options={referenceData.getByKey(ReferenceData.Country)?.values.map(x => x.key)}
                        getOptionLabel={(option: string) => referenceData.getByKey(ReferenceData.Country)?.values.getValueByKey(option)}
                        renderInput={(params: any) =>
                            <MuiTextField
                                {...params}
                                fullWidth
                                variant="outlined"
                                label="Purchase country"
                            />
                        }
                    />
                </Grid>
                <Grid item xs={4}>
                    <Field
                        component={DatePicker}
                        name="purchaseDate"
                        fullWidth
                        format="LL"
                        inputVariant="outlined"
                        label="Purchase date"
                        clearable
                    />
                </Grid>

                <Grid item xs={4}>
                    <Typography variant="body2" color={form.touched.productIsUnderWarranty && form.errors.productIsUnderWarranty ? "error" : "textSecondary"}>Product is under warranty?</Typography>
                    <RadioGroup
                        row
                        value={form.values.productIsUnderWarranty}
                        onChange={(e) => { form.setFieldValue('productIsUnderWarranty', e.target.value) }}
                    >
                        <FormControlLabel value='true' control={<Radio />} label="Yes" />
                        <FormControlLabel value='false' control={<Radio />} label="No" />
                    </RadioGroup>
                    <ErrorMessage name="productIsUnderWarranty">
                        {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                    </ErrorMessage>
                </Grid>
                <Grid item xs={4}>
                    <Typography variant="body2" color={form.touched.hasPurchaseReceipt && form.errors.hasPurchaseReceipt ? "error" : "textSecondary"}>Have you got end user purchase receipt?</Typography>
                    <RadioGroup
                        row
                        value={form.values.hasPurchaseReceipt}
                        onChange={(e) => { form.setFieldValue('hasPurchaseReceipt', e.target.value) }}
                    >
                        <FormControlLabel value={'true'} control={<Radio />} label="Yes" />
                        <FormControlLabel value={'false'} control={<Radio />} label="No" />
                    </RadioGroup>
                    <ErrorMessage name="hasPurchaseReceipt">
                        {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                    </ErrorMessage>
                    {form.values.hasPurchaseReceipt === 'true' &&
                        <Typography variant="caption" color={"secondary"}>Remember to upload the purchase receipt</Typography>
                    }
                </Grid>


                <Grid item xs={4}>
                    <Typography variant="body2" color={form.touched.canYouRepairProductYourself && form.errors.canYouRepairProductYourself ? "error" : "textSecondary"}>Can you repair product yourself?</Typography>
                    <RadioGroup
                        row
                        value={form.values.canYouRepairProductYourself}
                        onChange={(e) => { form.setFieldValue('canYouRepairProductYourself', e.target.value) }}
                    >
                        <FormControlLabel value='true' control={<Radio />} label="Yes" />
                        <FormControlLabel value='false' control={<Radio />} label="No" />
                    </RadioGroup>
                    <ErrorMessage name="canYouRepairProductYourself">
                        {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                    </ErrorMessage>
                </Grid>

                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        name="serialNumber"
                        fullWidth
                        variant="outlined"
                        label="Serial Number"
                        helperText={form.values.hasSerialNumber ? 'Remember to provide serial number' : ''}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        name="suggestedAction"
                        fullWidth
                        variant="outlined"
                        label="Suggested Action"
                    />
                </Grid>
                {/* End customers has registered product on warranty website */}
                <Grid item xs={12}>
                    <FormControlLabel
                        label="End customers has registered product on warranty website"
                        name="endCustomersHasRegisteredProduct"
                        value={form.values.endCustomersHasRegisteredProduct}
                        checked={form.values.endCustomersHasRegisteredProduct}
                        onChange={(e: any) => form.setFieldValue('endCustomersHasRegisteredProduct', e.target.checked)}
                        control={<Field type="checkbox" name="endCustomersHasRegisteredProduct" component={Checkbox} />}
                    />
                </Grid>
                {form.values.endCustomersHasRegisteredProduct &&
                    <>
                        {/* End customer email address */}
                        <Grid item xs={6}>
                            <Field
                                component={TextField}
                                name="endCustomerEmailAddress"
                                fullWidth
                                type="email"
                                variant="outlined"
                                label="End customer email address"
                            />
                        </Grid>

                        {/* Product QR Code */}
                        <Grid item xs={6}>
                            <Field
                                component={TextField}
                                name="productQRCode"
                                fullWidth
                                variant="outlined"
                                label="Product QR Code"
                            />
                        </Grid>
                    </>
                }
            </Grid>
        </Grid>
    )
}

export default ProductInfo;